import { Cookies } from 'react-cookie';

import { LocalStorageKeys } from 'app/enum';
import { AuthContext, AuthTokens } from 'app/model';
import { Taxonomy } from 'app/model/taxonomy';

import { TimingConstants } from '../constants/timing-constants';
import { environment } from '../services/environment';

const cookieService = new Cookies();
const RENEW_TIME = 30; // refresh the token if it expires within this number of seconds

export class SecurityUtils {
    public static getToken(): string {
        return (cookieService.get('token') as string) || '';
    }

    public static getRefreshToken(): string {
        return (cookieService.get('refresh_token') as string) || '';
    }

    public static isTokenValid(): boolean {
        const expires = localStorage.getItem(LocalStorageKeys.TOKEN_EXPIRES);
        if (!cookieService.get('token') || !expires) {
            return false;
        }

        // return new Date().getTime() < Number(expires);
        // todo proper tracking of expiration time
        return true;
    }

    public static tokenExpiresSoon(): boolean {
        const expires = localStorage.getItem(LocalStorageKeys.TOKEN_EXPIRES);

        return new Date().getTime() > Number(expires) - RENEW_TIME * TimingConstants.MS_IN_SEC;
    }

    public static tokenExpired(): boolean {
        const expires = localStorage.getItem(LocalStorageKeys.TOKEN_EXPIRES);

        return new Date().getTime() > Number(expires);
    }

    public static clearToken(): void {
        cookieService.remove('token', { path: environment.publicUrlPart });
        cookieService.remove('refresh_token', { path: environment.publicUrlPart });
        localStorage.setItem(LocalStorageKeys.TOKEN_ROLES, '[]');
    }

    public static setTokens(tokens: AuthTokens): void {
        // set cookies for UI path only, this way the won't get unnecessarily sent to API calls
        cookieService.set('token', tokens.token, { path: environment.publicUrlPart, sameSite: 'strict' });
        cookieService.set('refresh_token', tokens.refresh_token, {
            path: environment.publicUrlPart,
            sameSite: 'strict',
        });

        if (environment.debugToolbar) {
            // in dev add them also to the debug toolbar and profiler routes
            // as they need to be authorized with a cookie to be accessible
            cookieService.set('token', tokens.token, { path: '/_wdt', sameSite: 'strict' });
            cookieService.set('token', tokens.token, { path: '/_profiler', sameSite: 'strict' });
            // cypress does not seem to respect paths properly, so for now going to set it to anything
            cookieService.set('token', tokens.token, { path: '/', sameSite: 'strict' });
        }

        localStorage.setItem(
            LocalStorageKeys.TOKEN_EXPIRES,
            `${new Date().getTime() + environment.tokenExpire * TimingConstants.MS_IN_SEC}`,
        );
        localStorage.setItem(LocalStorageKeys.TOKEN_ROLES, JSON.stringify(tokens.roles));
    }

    public static getStoredTaxonomies(): Taxonomy[] | undefined {
        try {
            const data = JSON.parse(localStorage.getItem(LocalStorageKeys.ACTIVE_TAXONOMY) || 'null') as unknown;

            if (
                !data ||
                'object' !== typeof data ||
                (0 === Object.keys(data).length && !Array.isArray(data)) ||
                (Array.isArray(data) && 0 === Object.keys((data as Taxonomy[])[0]).length)
            ) {
                return undefined;
            }

            return Array.isArray(data) ? (data as Taxonomy[]) : ([data] as Taxonomy[]);
        } catch (err) {
            // bad json
            return undefined;
        }
    }

    public static setStoredTaxonomies(taxonomies: Taxonomy[]): void {
        localStorage.setItem(LocalStorageKeys.ACTIVE_TAXONOMY, JSON.stringify(taxonomies));
    }

    public static clearStoredTaxonomies(): void {
        localStorage.removeItem(LocalStorageKeys.ACTIVE_TAXONOMY);
    }

    public static buildAuthContext(): AuthContext {
        const roles: Record<string, boolean> = {};
        try {
            const rolesList = JSON.parse(localStorage.getItem(LocalStorageKeys.TOKEN_ROLES) || '[]') as string[];
            rolesList.forEach((role) => (roles[role] = true));
        } catch (err) {
            // ignored
        }

        return {
            valid: SecurityUtils.isTokenValid(),
            roles,
            taxonomies: [], // todo put list of taxonomies
        };
    }
}
